import React, { useState } from 'react';
import styles from './YearlyIncomeModal.module.css';
import Button from './Button';

interface YearlyIncomeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onComplete: (income: number) => void;
}

const YearlyIncomeModal: React.FC<YearlyIncomeModalProps> = ({ 
  isOpen, 
  onClose, 
  onComplete 
}) => {
  const [income, setIncome] = useState<string>('');
  const [showSuccess, setShowSuccess] = useState<boolean>(false);
  const [showBudgetBreakdown, setShowBudgetBreakdown] = useState<boolean>(false);
  const [budgetData, setBudgetData] = useState<{
    monthly: number;
    needs: number;
    wants: number;
    savings: number;
  } | null>(null);

  if (!isOpen) {
    return null;
  }

  const calculateBudget = (yearlyIncome: number) => {
    const monthlyIncome = yearlyIncome / 12;
    return {
      monthly: monthlyIncome,
      needs: monthlyIncome * 0.5,      // 50% for needs
      wants: monthlyIncome * 0.3,      // 30% for wants
      savings: monthlyIncome * 0.2     // 20% for savings
    };
  };

  const handleSubmit = () => {
    const numericIncome = parseFloat(income);
    if (!isNaN(numericIncome) && numericIncome > 0) {
      const budget = calculateBudget(numericIncome);
      setBudgetData(budget);
      setShowBudgetBreakdown(true);
    }
  };

  const handleComplete = () => {
    if (budgetData) {
      // Store yearly income and budget data in localStorage
      const yearlyIncome = parseFloat(income);
      localStorage.setItem('yearly_income', yearlyIncome.toString());
      localStorage.setItem('yearly_income_budget', JSON.stringify(budgetData));

      setShowSuccess(true);
      // Start fireworks animation
      setTimeout(() => {
        onComplete(yearlyIncome);
        setShowSuccess(false);
        setShowBudgetBreakdown(false);
        setBudgetData(null);
        setIncome('');
      }, 3000); // Show congratulations for 3 seconds
    }
  };

  const handleClose = () => {
    setIncome('');
    setShowSuccess(false);
    setShowBudgetBreakdown(false);
    setBudgetData(null);
    onClose();
  };

  if (showSuccess) {
    return (
      <div className={styles.modalOverlay}>
        <div className={styles.modalContent}>
          <div className={styles.successContainer}>
            <div className={styles.fireworks}>
              <div className={styles.firework}></div>
              <div className={styles.firework}></div>
              <div className={styles.firework}></div>
            </div>
            <h2 className={styles.congratsTitle}>Congratulations!</h2>
            <p className={styles.congratsMessage}>
              You're on your way to financial mastery!
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (showBudgetBreakdown && budgetData) {
    return (
      <div className={styles.modalOverlay}>
        <div className={styles.modalContent}>
          <h2>Your Monthly Budget (50/30/20 Rule)</h2>
          <div className={styles.budgetContainer}>
            <div className={styles.budgetItem}>
              <div className={styles.budgetLabel}>Monthly Income</div>
              <div className={styles.budgetAmount}>
                ${budgetData.monthly.toFixed(2)}
              </div>
            </div>
            
            <div className={styles.budgetBreakdown}>
              <div className={styles.budgetCategory + ' ' + styles.needs}>
                <div className={styles.categoryHeader}>
                  <span className={styles.percentage}>50%</span>
                  <span className={styles.categoryName}>Needs</span>
                </div>
                <div className={styles.categoryAmount}>${budgetData.needs.toFixed(2)}</div>
                <div className={styles.categoryDescription}>
                  Rent, groceries, utilities, minimum debt payments
                </div>
              </div>

              <div className={styles.budgetCategory + ' ' + styles.wants}>
                <div className={styles.categoryHeader}>
                  <span className={styles.percentage}>30%</span>
                  <span className={styles.categoryName}>Wants</span>
                </div>
                <div className={styles.categoryAmount}>${budgetData.wants.toFixed(2)}</div>
                <div className={styles.categoryDescription}>
                  Dining out, entertainment, hobbies, non-essential shopping
                </div>
              </div>

              <div className={styles.budgetCategory + ' ' + styles.savings}>
                <div className={styles.categoryHeader}>
                  <span className={styles.percentage}>20%</span>
                  <span className={styles.categoryName}>Savings</span>
                </div>
                <div className={styles.categoryAmount}>${budgetData.savings.toFixed(2)}</div>
                <div className={styles.categoryDescription}>
                  Emergency fund, retirement, debt payoff, investments
                </div>
              </div>
            </div>
          </div>
          
          <div className={styles.buttonContainer}>
            <Button onClick={handleComplete} className={styles.completeButton}>
              Complete Quest
            </Button>
            <Button onClick={handleClose} className={styles.backButton}>
              Back
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modalContent}>
        <h2>What is your yearly income?</h2>
        <div className={styles.inputContainer}>
          <div className={styles.inputWrapper}>
            <span className={styles.dollarSign}>$</span>
            <input
              type="number"
              step="1"
              min="0"
              value={income}
              onChange={(e) => setIncome(e.target.value)}
              placeholder="0"
              className={styles.incomeInput}
              autoFocus
            />
          </div>
        </div>
        <div className={styles.buttonContainer}>
          <Button 
            onClick={handleSubmit} 
            disabled={!income || parseFloat(income) <= 0}
            className={styles.doneButton}
          >
            Calculate Budget
          </Button>
          <Button onClick={handleClose} className={styles.cancelButton}>
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
};

export default YearlyIncomeModal;